import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Home, X } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Greg's personal receipts and charges waiting on the Household site's Review tab.
 *
 * Greg, 2026-10-26: "integrate an alert for me ... like it does where it shows me at the top
 * that 15 invoices are waiting ... I should just be able to easily go clear my personal receipts
 * every day." Same look and behaviour as ApprovalsWaitingBanner (polls, re-checks on focus,
 * dismiss hides only this count). The server answers with a count for Greg and 0 for anyone
 * else, and never sends the details: those stay on cain-household.netlify.app.
 */
export default function HouseholdWaitingBanner({ onOpen }) {
  const [state, setState] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(null);
  const lastLoad = useRef(0);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    lastLoad.current = Date.now();
    try {
      const base = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
      const token = getAccessToken();
      const r = await fetch(`${base}/household-waiting`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setState(r.ok ? await r.json() : null);
    } catch {
      setState(null);
    }
  }, []);

  useEffect(() => {
    load();
    // Five minutes: these change once a day with the bank feed, or when Greg approves.
    const timer = setInterval(load, 5 * 60_000);
    const onFocus = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastLoad.current < 60_000) return;
      load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  if (isMobile) return null;
  const count = Number(state && state.count) || 0;
  if (!count || dismissedAt === count) return null;
  const oldest = Number(state && state.oldestDays) || 0;

  return (
    <div style={{
      background: '#f2f6fb', borderBottom: '1px solid #cfdcea', color: '#1f4e79',
      fontSize: 13, padding: '8px 14px', display: 'flex', alignItems: 'center',
      gap: 10, flexShrink: 0, fontFamily: 'inherit',
    }}>
      <Home size={16} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>{count} personal {count === 1 ? 'item is' : 'items are'} waiting for your approval</strong>
        {oldest >= 3 && <> · the oldest is <strong>{oldest} days</strong> old</>}
      </span>
      <button
        onClick={() => { try { sessionStorage.setItem('household-tab', 'review'); } catch { /* ok */ } if (onOpen) onOpen(); }}
        className="btn btn-outline"
        style={{ fontSize: 12, padding: '3px 10px', flexShrink: 0 }}
      >
        Review them
      </button>
      <button
        onClick={() => setDismissedAt(count)}
        title="Hide until something changes"
        style={{ background: 'none', border: 'none', color: '#1f4e79', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
